import { useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { craftImages } from '@/mocks/home';
import { cdn } from '@/config/cdn';

gsap.registerPlugin(ScrollTrigger);

function Chapter({ n, title, text }: { n: string; title: string; text: string }) {
  return (
    <figcaption className="mt-5 grid grid-cols-[32px_minmax(0,1fr)] gap-x-4 max-w-[420px]">
      <span className="fold-label text-[rgba(245,241,235,0.45)] pt-1">{n}</span>
      <div>
        <p className="fold-serif text-xl md:text-2xl">{title}</p>
        <p className="mt-2 text-sm leading-relaxed text-[rgba(245,241,235,0.6)]">{text}</p>
      </div>
    </figcaption>
  );
}

export default function Craft() {
  const { hands, lamination, finished } = craftImages;
  const rootRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const mm = gsap.matchMedia();
    const ctx = gsap.context(() => {
      mm.add(
        {
          desktop: '(min-width: 1024px) and (prefers-reduced-motion: no-preference)',
          tablet: '(min-width: 768px) and (max-width: 1023px) and (prefers-reduced-motion: no-preference)',
        },
        () => {

          // Native "window over fixed backdrop": image is position:fixed (browser-composited,
          // zero per-scroll JS) and the frame's clip-path acts as the window.
          // Only horizontal placement is measured, and only on resize/refresh.
          const frames: Array<{ img: HTMLElement; frame: HTMLElement }> = [];
          const windowed = (sel: string) => {
            const img = root.querySelector<HTMLElement>(sel);
            const frame = img?.parentElement;
            if (!img || !frame) return;
            frames.push({ img, frame });
            gsap.set(frame, { position: 'relative', clipPath: 'inset(0px 0px 0px 0px)' });
            gsap.set(img, { position: 'fixed', top: 0, height: '100vh', willChange: 'auto' });
          };
          const place = () => {
            frames.forEach(({ img, frame }) => {
              const r = frame.getBoundingClientRect();
              gsap.set(img, { left: r.left, width: r.width });
            });
          };
          windowed('[data-craft-a]');
          windowed('[data-craft-b]');
          windowed('[data-craft-film-img]');
          place();
          ScrollTrigger.addEventListener('refresh', place);
          window.addEventListener('resize', place);

          // Scene C: frame opens from shell width to viewport edges
          const frame = root.querySelector<HTMLElement>('[data-craft-frame]');
          const shell = root.querySelector<HTMLElement>('[data-craft-shell]');
          if (frame && shell) {
            const inset = () => {
              const fw = frame.clientWidth;
              const sw = shell.clientWidth;
              const side = Math.max(0, (fw - sw) / 2);
              return `inset(8% ${side}px 8% ${side}px)`;
            };
            gsap.fromTo(frame, { clipPath: inset }, {
              clipPath: 'inset(0% 0px 0% 0px)',
              ease: 'none',
              scrollTrigger: { trigger: frame, start: 'top 95%', end: 'top 10%', scrub: true, invalidateOnRefresh: true, refreshPriority: -10 },
            });
          }
          ScrollTrigger.sort();
          return () => {
            ScrollTrigger.removeEventListener('refresh', place);
            window.removeEventListener('resize', place);
          };
        },
      );
    }, root);
    return () => { mm.revert(); ctx.revert(); };
  }, []);

  return (
    <section ref={rootRef} id="craft" className="bg-[#171515] text-[#F5F1EB] pt-24 md:pt-40 pb-0 overflow-x-clip" aria-labelledby="craft-title">
      <div className="fold-shell" data-craft-shell>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-x-6 gap-y-10 items-end">
          <h2 id="craft-title" className="fold-serif uppercase md:col-span-8 text-[13vw] md:text-[7vw] xl:text-[6.5rem] leading-[0.9]">
            Crafted<br />with <span className="italic">patience.</span>
          </h2>
          <p className="md:col-span-3 md:col-start-10 text-sm md:text-[15px] leading-relaxed text-[rgba(245,241,235,0.65)]">
            Three days between the first turn and the oven. One story, in three frames.
          </p>
        </div>

        <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-12 gap-x-6 gap-y-14">
          <figure className="md:col-span-5 md:mt-32 min-w-0">
            <div className="w-full aspect-[4/5] overflow-hidden">
              <img data-craft-a src={cdn(hands.src)} alt={hands.alt} title="Hand folding laminated dough at FOLD." className="w-full h-full object-cover object-top" />
            </div>
            <Chapter n="01" title="The fold." text="Cold butter locked into dough by hand, turned and rested." />
          </figure>

          <figure className="md:col-span-7 min-w-0">
            <div className="w-full aspect-[4/5] md:aspect-[6/7] overflow-hidden">
              <img data-craft-b src={cdn(lamination.src)} alt={lamination.alt} title="Laminated dough layers at FOLD." className="w-full h-full object-cover object-center" />
            </div>
            <Chapter n="02" title="Twenty-seven layers." text="Butter and dough, sheet over sheet — the structure you hear when it shatters." />
          </figure>
        </div>
      </div>

      {/* conclusion — breaks the shell; reserved slot for a future scroll-controlled baking film */}
      <figure className="mt-20 md:mt-32" data-craft-film>
        <div data-craft-frame className="w-full h-[60vw] md:h-[min(56vw,820px)] overflow-hidden bg-[#0F0E0E]">
          <img data-craft-film-img src={cdn(finished.src)} alt={finished.alt} title="Finished FOLD. croissants" className="w-full h-full object-cover object-center" />
        </div>
        <div className="fold-shell py-8 md:py-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <Chapter n="03" title="Out of the deck at dawn." text="Golden, crackling, gone by three." />
          <Link to="/story" className="fold-label fold-link text-[#F5F1EB] whitespace-nowrap cursor-pointer self-start sm:self-end">
            Read our story
          </Link>
        </div>
      </figure>
    </section>
  );
}